import { NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DsButtonComponent } from '@design-system/ds-button/component/ds-button.component';
import { DsIconComponent } from '@design-system/ds-icon/component/ds-icon.component';
import { DsInputComponent } from '@design-system/ds-input/component/ds-input.component';
import { DsTextComponent } from '@design-system/ds-text/component/ds-text.component';
import { DsTextareaComponent } from '@design-system/ds-textarea/component/ds-textarea.component';
import { extractHttpErrorMessage } from '@utils/extract-http-error-message.helper';
import { finalize, startWith, Subject, take, takeUntil } from 'rxjs';
import { VerifyIbRepositionPayload } from '../../../../ib-repo-verification/interfaces/verify-ib-reposition-payload.interface';
import {
  IB_REPO_NOTE_MAX_LENGTH,
  IB_REPO_VERIFIED_HOURS_VALIDATORS,
} from '../../../../ib-repo-verification/properties/ib-repo-verification.properties';
import { IbRepoVerificationService } from '../../../../ib-repo-verification/services/ib-repo-verification.service';
import { IbRepoVerificationStateInterface } from '../../../interfaces/ib-repo-verification-state.interface';

/**
 * The inline form that verifies the IB repo hours.
 * The form saves the verification at once, apart from the record save.
 */
@Component({
  selector: 'app-btr-ib-repo-verification-panel',
  templateUrl: './btr-ib-repo-verification-panel.component.html',
  styleUrls: ['./btr-ib-repo-verification-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgIf,
    ReactiveFormsModule,
    MatFormFieldModule,
    DsButtonComponent,
    DsIconComponent,
    DsInputComponent,
    DsTextComponent,
    DsTextareaComponent,
  ],
})
export class BtrIbRepoVerificationPanelComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() blockTimeRecordId = '';
  @Input() state: IbRepoVerificationStateInterface | null = null;
  @Input() panelId = 'btr_ib_repo_panel';
  @Output() readonly verified = new EventEmitter<VerifyIbRepositionPayload>();
  @Output() readonly cancelled = new EventEmitter<void>();
  /** Emits true when the verification request starts and false when it ends, so the modal cannot close mid-request. */
  @Output() readonly pendingChange = new EventEmitter<boolean>();

  @ViewChild(DsInputComponent) private readonly hoursInput?: DsInputComponent;

  private readonly fb = inject(NonNullableFormBuilder);
  private readonly service = inject(IbRepoVerificationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  readonly noteMaxLength = IB_REPO_NOTE_MAX_LENGTH;
  readonly form = this.fb.group({
    verifiedHours: this.fb.control<number | null>(null, [...IB_REPO_VERIFIED_HOURS_VALIDATORS]),
    note: this.fb.control<string | null>(null, [Validators.maxLength(IB_REPO_NOTE_MAX_LENGTH)]),
  });

  isPending = false;
  isInvalid = true;
  errorMessage: string | null = null;
  noteCounter = `0 / ${IB_REPO_NOTE_MAX_LENGTH}`;

  ngOnInit(): void {
    // The form emits a status after each value change, so the counter updates on each key press.
    this.form.statusChanges.pipe(startWith(this.form.status), takeUntil(this.destroy$)).subscribe(() => {
      this.isInvalid = this.form.invalid;
      this.noteCounter = `${this.form.controls.note.value?.length ?? 0} / ${IB_REPO_NOTE_MAX_LENGTH}`;
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(): void {
    this.form.reset({ verifiedHours: this.state?.initialHours ?? null, note: this.state?.note ?? null });
  }

  ngAfterViewInit(): void {
    // The focus changes the state of the form field, so it must wait until the current check ends.
    setTimeout(() => this.hoursInput?.focus());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  confirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isPending) {
      return;
    }

    const { verifiedHours, note } = this.form.getRawValue();
    const payload: VerifyIbRepositionPayload = { verifiedHours: Number(verifiedHours), note: note?.trim() || null };

    this.isPending = true;
    this.pendingChange.emit(true);
    this.errorMessage = null;

    this.service
      .verify(this.blockTimeRecordId, payload)
      .pipe(
        take(1),
        finalize(() => {
          this.isPending = false;
          this.pendingChange.emit(false);
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => this.verified.emit(payload),
        error: (error: unknown) => {
          this.errorMessage = extractHttpErrorMessage(error, 'Failed to save the verification. Please try again.');
          this.cdr.markForCheck();
        },
      });
  }
}
